import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { OptionProps, ToolBar, OVOrderHeader } from 'components'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { QuotaMonth } from 'container/SalesOrder/interfaces'

import { useDispatch, useSelector } from 'store/store'
import { useNavigate, useMatch } from 'react-router-dom'
import { getClientOvTypes } from 'services/client/ovTypes'
import { getClientCredit } from 'services/client/credit'
import { ClientCrediInfo } from 'services/client/credit/interfaces'
import { getClientAddress } from 'services/salesOrder'
import { getPaymentsTerms, postMaintainDraft, getLastHeader } from 'services/draft'
import {
  asyncGetCorpoRascunho,
  getItems,
  setNumberDraft
} from 'store/SalesOrderContext/salesOrderSlice'
import { URL_CONST } from 'constants/urls'
import { Cliente } from 'container/CreateOrder'
import { ClientsListInterface } from 'container/SalesOrder/components/ModalPostOn'
import { isObjectEmpty } from 'commons/utils/object'
import { getUser } from 'commons/utils/user'
import { handleDiscountInput } from './utils'
import { useCallbackPrompt } from 'commons/customHooks/useCallbackPrompt'
import {
  setConfirmedNavigation,
  setShowPrompt
} from 'store/StructuralContext/structuralSlice'
import { TypesValidations } from 'constants/enums'
import { getActiveQuotaMonths } from 'services/quota_month'
import { formatDate, getLastDayOfMonth } from 'commons/utils/date'
import { availableCentersFactories, OVOrder } from 'constants/enums'

interface TopSectionProps {
  setLoading?: Dispatch<SetStateAction<boolean>>
  quotasMonth?: QuotaMonth[]
  defaultPayment?: string
  defaultClient?: Pick<
    Cliente,
    | 'cd_clie'
    | 'nm_clie'
    | 'cd_empr'
    | 'cd_orgn_vend'
    | 'cd_canl_dstb'
    | 'cd_seto_atvd'
    | 'cd_regi_vend'
    | 'ds_regi_vend'
    | 'nr_cnpj_cpf'
    | 'cd_escr_vend'
  >
  orderNumber?: string
  materialTypeMTO?: boolean
  onClickDelete?: () => void
  onClickDuplicate?: () => void
  onClickPostOn?: (array: ClientsListInterface[]) => Promise<void>
  disableClientConfig?: boolean
  disableOrderConfig?: boolean
  disableFields?: boolean
  showDeleteModal?: boolean
  isButtonRescueData?: boolean
  disableToolbar?: boolean
  hasOvs?: boolean
  step?: number
  isEDI?: boolean
}

export interface PaymentMethodsProps {
  rowscount: number
  cd_cond_pgto: string
  nr_dia_limt: number
  ds_cond_pgto: string
  nr_dias_adic: number
}

export default function TopSection({
  setLoading = () => {
    return
  },
  quotasMonth = [],
  defaultPayment,
  defaultClient,
  orderNumber,
  materialTypeMTO,
  onClickDelete = () => {
    return
  },
  onClickDuplicate = () => {
    return
  },
  onClickPostOn,
  disableClientConfig = false,
  disableFields = false,
  disableOrderConfig = false,
  showDeleteModal = true,
  isButtonRescueData = false,
  disableToolbar = false,
  hasOvs = false,
  step,
  isEDI = false
}: TopSectionProps) {
  const { setValue, getValues, formState, trigger, watch, reset } = useFormContext()
  const matchNewOrder = useMatch(URL_CONST.NEW_ORDERS)

  const dt_ano_mes_cota = watch('dt_ano_mes_cota')

  const myClient = watch('cd_clie')

  const noClient = !Object.entries(getValues('cd_clie')).length

  const { errors, isDirty } = formState
  useCallbackPrompt(isDirty)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const draftBody = useSelector((state) => state.salesOrder.draftBody)

  const [isDisableButtonSave, setIsDisableButtonSave] = useState(false)
  const [addressOptions, setAddressOptions] = useState<OptionProps[]>([])
  const [inputAutocomplete, setInputAutocomplete] = useState('')
  const [inputAutocompleteSaleRegion, setInputAutocompleteSaleRegion] = useState('')
  const [clientOvTypes, setClientOvTypes] = useState<OptionProps[]>([])
  const [clientCredit, setClientCredit] = useState<ClientCrediInfo>({} as ClientCrediInfo)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsProps[]>([])
  const isMounted = useRef(false)

  function handleDelete() {
    onClickDelete()
    dispatch(setConfirmedNavigation(true))
    navigate(URL_CONST.ROOT)
    dispatch(setShowPrompt(false))
  }

  const isDisableButtonRescueData = !Object.entries(myClient)?.length
  const onClickRescueData = async () => {
    const query = {
      numeroPedido: getValues('nr_pedi_rasc'),
      codigoCliente: myClient?.cd_clie ?? myClient,
      preparacaoMto: getValues('fl_mto_prep') === TypesValidations.YES
    }
    await getLastHeader(query)
      .then((response) => {
        if (response) {
          setValue('cd_tipo_orde', response.TipoOrdemVenda)
          if (response.CondicaoPagamento?.trim()) {
            setValue('cd_cond_pgto', response.CondicaoPagamento)
            const paymentOption = paymentMethods.find(
              (payment) => payment.cd_cond_pgto === response.CondicaoPagamento
            )
            setValue('ds_cond_pgto', paymentOption?.ds_cond_pgto)
            setInputAutocomplete(
              `${response.CondicaoPagamento} - ${paymentOption?.ds_cond_pgto}`
            )
          }
          setValue('nr_dias_adic', response.PrazoExtra)
          const currentValue = getValues('pc_dsct') as unknown as number
          const pc_dsct = handleDiscountInput(response.Desconto, currentValue)
          setValue('pc_dsct', pc_dsct)
          setValue('ds_text_item_mto', response.TextoItemMto)
        }
      })
      .catch(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (defaultPayment) setInputAutocomplete(defaultPayment)
    const getCondicoesPagamento = async () => {
      const returnApi = await getPaymentsTerms({
        qt_rowcount: 999,
        cd_empr: '',
        cd_orgn_vend: '',
        cd_canl_dstb: '',
        cd_tipo_orde: '',
        fl_orde_tele_vend: TypesValidations.NO,
        cd_cond_pgto: ''
      })

      if (returnApi) {
        localStorage.setItem('paymentMethods', JSON.stringify(returnApi))
        setPaymentMethods(returnApi)
      }
    }

    getCondicoesPagamento()

    if (defaultClient?.cd_clie) {
      const { cd_canl_dstb, cd_clie, cd_empr, cd_orgn_vend, cd_seto_atvd, cd_escr_vend } =
        defaultClient

      getClientAddress({
        cd_empr,
        cd_orgn_vend,
        cd_canl_dstb,
        cd_seto_atvd,
        cd_clie
      }).then((response) => {
        setAddressOptions(
          response.map((add) => ({
            value: add.cd_pcro,
            title: add.ds_endr
          }))
        )
      })

      // TODO: Check if necessary. Probably duplicated or bad location
      getClientOvTypes(cd_clie, cd_escr_vend).then((types) => {
        const ovTypes = types?.map((type: string) => {
          return { title: type, value: type }
        })

        setClientOvTypes(ovTypes)
      })
    }
    if (defaultClient?.cd_regi_vend && defaultClient?.ds_regi_vend) {
      setInputAutocompleteSaleRegion(
        `${defaultClient.cd_regi_vend} - ${defaultClient?.ds_regi_vend}`
      )
    }

    const getCreditData = async () => {
      if (defaultClient?.cd_clie) {
        const credit = await getClientCredit(defaultClient.cd_clie)

        if (isMounted.current) {
          setClientCredit(credit)
        }
      }
    }

    getCreditData()
  }, [])

  const postDraft = async () => {
    setLoading(true)
    trigger()
    if (!isObjectEmpty(errors) && !matchNewOrder) {
      toast.error('Campos em vermelho são obrigatórios!')
      return
    }
    setIsDisableButtonSave(true)
    const form = getValues()
    const {
      cd_clie: {
        cd_canl_dstb,
        cd_clie,
        cd_empr,
        cd_equi_vend,
        cd_escr_vend,
        cd_orgn_vend,
        cd_regi_vend
      },
      address,
      cd_seto_atvd,
      cd_tipo_orde,
      fl_mto_prep,
      pc_dsct,
      fl_dsct_matr,
      fl_prmt_desd,
      cd_cond_pgto,
      ds_cond_pgto,
      nr_pedi_clie,
      nr_dias_adic,
      cd_clie_entr,
      nr_pedi_rasc,
      ds_text_nota_fisc,
      fl_matr_fora_linh,
      dt_dsjd_pedi,
      ds_text_item_mto
    } = form

    const getQuotaMonth = async (): Promise<string> => {
      const quotaMonths = await getActiveQuotaMonths(cd_escr_vend)
      if (!quotaMonths.length) return ''

      return quotaMonths[0].Descricao
    }

    const currentQuotaMonth = await getQuotaMonth()

    const bodyParamsMaintainRascunho = {
      cd_canl_dstb: cd_canl_dstb || '',
      cd_clie: cd_clie || '',
      cd_empr: cd_empr || '',
      cd_equi_vend: cd_equi_vend || '',
      cd_escr_vend: cd_escr_vend || '',
      cd_orgn_vend: cd_orgn_vend || '',
      cd_regi_vend: cd_regi_vend || '',
      cd_seto_atvd: cd_seto_atvd || '',
      nr_pedi_rasc: nr_pedi_rasc || '',
      cd_tipo_orde,
      nr_dias_adic,
      nr_pedi_clie,
      nr_pedi_cpra: '',
      cd_clie_entr: address || cd_clie_entr || '',
      ds_text_item_mto: ds_text_item_mto || '',
      fl_matr_fora_linh: fl_matr_fora_linh || TypesValidations.NO,
      fl_mto_prep,
      fl_dsct_matr: fl_dsct_matr,
      fl_prmt_desd,
      pc_dsct,
      ds_item_pedi_rasc: [],
      cd_usua_alte: getUser(),
      cd_cent_prio: availableCentersFactories,
      cd_cond_pgto,
      ds_cond_pgto,
      dt_ano_mes_cota: dt_ano_mes_cota
        ? dt_ano_mes_cota
        : formatDate(currentQuotaMonth, 'YYYYMM'),
      dt_dsjd_pedi: dt_dsjd_pedi
        ? dt_dsjd_pedi
        : getLastDayOfMonth(currentQuotaMonth, 'YYYY-MM-DD'),
      ds_text_nota_fisc: ds_text_nota_fisc
    }

    await postMaintainDraft(bodyParamsMaintainRascunho)
      .then((response) => {
        if (response && response.nr_pedi_rasc) {
          const idDraft = response.nr_pedi_rasc.toString().trim()
          const successCallback = () =>
            matchNewOrder ? navigate(`${URL_CONST.ORDERS}/${idDraft}`) : setLoading(false)
          dispatch(setNumberDraft(idDraft))
          dispatch(asyncGetCorpoRascunho(idDraft, successCallback))
          setIsDisableButtonSave(false)
          toast.success('Pedido salvo com sucesso!')
          dispatch(getItems(''))
        }
      })
      .catch(() => {
        setLoading(false)
        setIsDisableButtonSave(false)
      })
    reset(getValues())
  }

  return (
    <>
      <ToolBar
        orderNumber={orderNumber}
        materialTypeMTO={materialTypeMTO}
        isButtonRescueData={isButtonRescueData && !hasOvs}
        isDisableButtonRescueData={isDisableButtonRescueData}
        onClickRescueData={onClickRescueData}
        isButtonDelete={!hasOvs}
        // isButtonTrack // TODO: Remove this comment in the next release 61
        isButtonDuplicate
        isButtonPostOn
        isButtonSave={!hasOvs && step === 0}
        disableToolbar={disableToolbar}
        onClickSave={postDraft}
        onClickDuplicate={onClickDuplicate}
        onClickPostOn={onClickPostOn}
        onClickDelete={handleDelete}
        isDisableButtonPostOn={!orderNumber}
        isDisableButtonDuplicate={!orderNumber}
        isDisableButtonSave={isDisableButtonSave || noClient || disableOrderConfig}
        isDisableButtonDelete={!orderNumber || draftBody.cd_stat_pedi_rasc !== 0}
        isDisableButtonTrack={!orderNumber}
        showDeleteModal={showDeleteModal}
        fullWidth={true}
        isButtonPendingEDI={isEDI}
      />
      <OVOrderHeader
        type={OVOrder.ORDER}
        postDraft={postDraft}
        quotasMonth={quotasMonth}
        defaultClient={defaultClient}
        disableClientConfig={disableClientConfig}
        disableOrderConfig={disableOrderConfig}
        disableFields={disableFields}
        paymentMethods={paymentMethods}
        setSubHeader={setClientCredit}
        subHeader={clientCredit}
        addressOptions={addressOptions}
        clientOvTypes={clientOvTypes} // TODO: Check if necessary
        inputAutocomplete={inputAutocomplete}
        inputAutocompleteSaleRegion={inputAutocompleteSaleRegion}
        setAddressOptions={setAddressOptions}
        setClientOvTypes={setClientOvTypes}
        setInputAutocomplete={setInputAutocomplete}
        setInputAutocompleteSaleRegion={setInputAutocompleteSaleRegion}
        hasOvs={hasOvs}
      />
    </>
  )
}
